import { ReactNode, SyntheticEvent, useCallback } from 'react';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import { paragraphMixins } from 'design-system/mixins/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import IconCaret from 'images/icons/caret-down.svg';
import useToggle from 'lib/hooks/useToggle';

const TitleContainer = styled.div<{ disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.color.text.primary};

  ${paragraphMixins.lgBold}
`;
const StyledIconCaret = styled(({ disabled, expanded, ...props }) => <IconCaret {...props} />)<{
  disabled?: boolean;
  expanded: boolean;
}>`
  transition: transform ${TransitionDuration.Fast};
  transform: rotate(${({ expanded }) => (expanded ? '0' : '-180deg')});
  justify-self: center;

  rect[fill],
  path[fill] {
    fill: ${({ theme, disabled }) =>
      disabled ? theme.color.text.disabled : theme.color.text.primary};
  }
`;

interface AccordionProps {
  children?: ReactNode;
  count?: number;
  dataTest?: string;
  defaultExpanded?: boolean;
  disabled?: boolean;
  title: ReactNode;
}

const Accordion = ({
  children,
  count,
  dataTest,
  defaultExpanded = true,
  disabled,
  title,
}: AccordionProps) => {
  const [expanded, { toggle }] = useToggle(defaultExpanded);

  const handleClick = useCallback(
    (_event: SyntheticEvent<HTMLDivElement>) => {
      if (disabled) {
        return undefined;
      }

      return toggle();
    },
    [disabled, toggle],
  );

  return (
    <div aria-expanded={expanded ? 'true' : 'false'}>
      <TitleContainer disabled={disabled} id={dataTest} onClick={handleClick} data-test={dataTest}>
        <div>
          {title}
          {count != null && ` (${count})`}
        </div>
        <Spacer width={1} />
        <StyledIconCaret height='8px' width='16px' disabled={disabled} expanded={expanded} />
      </TitleContainer>

      {expanded && children}
    </div>
  );
};

export default Accordion;
